import { useEffect, useState } from "react";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MarketingTags Component
 * Fetches marketing tag settings from the backend and injects them into the document head.
 * Supports: Google Analytics 4, Google Tag Manager, Meta Pixel (Facebook)
 */
const MarketingTags = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadMarketingTags = async () => {
      try {
        const response = await fetch(`${API}/site-settings/public`);
        if (!response.ok) return;
        
        const settings = await response.json();
        
        // Google Analytics 4
        if (settings.google_analytics_id && !document.getElementById("ga4-script")) {
          const gaId = settings.google_analytics_id;
          
          // Load gtag.js
          const gtagScript = document.createElement("script");
          gtagScript.id = "ga4-script";
          gtagScript.async = true;
          gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
          document.head.appendChild(gtagScript);
          
          // Initialize gtag
          const gtagInit = document.createElement("script");
          gtagInit.id = "ga4-init";
          gtagInit.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaId}');
          `;
          document.head.appendChild(gtagInit);
          
        }
        
        // Google Tag Manager
        if (settings.google_tag_manager_id && !document.getElementById("gtm-script")) {
          const gtmId = settings.google_tag_manager_id;
          
          // GTM head script
          const gtmScript = document.createElement("script");
          gtmScript.id = "gtm-script";
          gtmScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
          `;
          document.head.appendChild(gtmScript);
          
          // GTM noscript (add to body)
          if (!document.getElementById("gtm-noscript")) {
            const gtmNoscript = document.createElement("noscript");
            gtmNoscript.id = "gtm-noscript";
            gtmNoscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            document.body.insertBefore(gtmNoscript, document.body.firstChild);
          }
          
        }
        
        // Meta Pixel (Facebook)
        if (settings.meta_pixel_id && !document.getElementById("meta-pixel-script")) {
          const pixelId = settings.meta_pixel_id;
          
          const metaScript = document.createElement("script");
          metaScript.id = "meta-pixel-script";
          metaScript.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${pixelId}');
            fbq('track', 'PageView');
          `;
          document.head.appendChild(metaScript);
          
          // Meta Pixel noscript
          if (!document.getElementById("meta-pixel-noscript")) {
            const metaNoscript = document.createElement("noscript");
            metaNoscript.id = "meta-pixel-noscript";
            metaNoscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
            document.body.appendChild(metaNoscript);
          }
          
        }
        
        // Custom head scripts
        if (settings.custom_head_scripts && !document.getElementById("custom-head-scripts")) {
          const sanitized = DOMPurify.sanitize(settings.custom_head_scripts, { ADD_TAGS: ["script"], ADD_ATTR: ["src", "async", "defer", "type"] });
          const customHead = document.createElement("div");
          customHead.id = "custom-head-scripts";
          customHead.innerHTML = sanitized;
          
          // Move script elements to head
          const scripts = customHead.querySelectorAll("script");
          scripts.forEach(script => {
            const newScript = document.createElement("script");
            if (script.src) {
              newScript.src = script.src;
              newScript.async = true;
            } else {
              newScript.innerHTML = script.innerHTML;
            }
            document.head.appendChild(newScript);
          });
          
        }
        
        // Custom body scripts
        if (settings.custom_body_scripts && !document.getElementById("custom-body-scripts")) {
          const sanitizedBody = DOMPurify.sanitize(settings.custom_body_scripts, { ADD_TAGS: ["script"], ADD_ATTR: ["src", "async", "defer", "type"] });
          const customBody = document.createElement("div");
          customBody.id = "custom-body-scripts";
          customBody.innerHTML = sanitizedBody;
          
          // Move script elements to end of body
          const scripts = customBody.querySelectorAll("script");
          scripts.forEach(script => {
            const newScript = document.createElement("script");
            if (script.src) {
              newScript.src = script.src;
              newScript.async = true;
            } else {
              newScript.innerHTML = script.innerHTML;
            }
            document.body.appendChild(newScript);
          });
          
        }
        
        setLoaded(true);
      } catch (error) {
        console.error("Request failed:", error);
      }
    };

    loadMarketingTags();
  }, []);

  // This component doesn't render anything visible
  return null;
};

export default MarketingTags;
